import React, { useEffect, useRef } from "react";
import { logException } from "@wss/error-tracking";
import BlogCouponCodesSignup from "../BlogCouponCodesSignup/BlogCouponCodesSignup";
import ContentLegalDisclaimer from "../../../common/ContentLegalDisclaimer/ContentLegalDisclaimer";
import BlogSocialMediaShareIcons from "../BlogSocialMediaShareIcons/BlogSocialMediaShareIcons";
import ExternalLinkModal from "~/common/ExternalLinkModal/ExternalLinkModal";
import BlogPostTitleSection from "./BlogPostTitleSection";
import ChildOrNull from "~/common/ChildOrNull";
import BlogRelatedResources from "./BlogRelatedResources";
import {
  addAttributesToDynamicFormContent,
  BlogPost,
  EmailSignupEndpointResponse,
  fullWidthLayoutId,
  getEmailElementsFromDynamicContent,
  getEmailFormDataFromDynamicBlogContent,
  updateDynamicFormContent,
} from "../utilities";

export interface BlogPostContentProps {
  post: BlogPost;
  displayLegalDisclaimer?: boolean;
  externalLinkModal?: boolean;
  isWideBlogLayout?: boolean;
  showTitle?: boolean;
}

const BlogPostContent = ({
  post,
  displayLegalDisclaimer = true,
  externalLinkModal = true,
  isWideBlogLayout = false,
  showTitle = true,
}: Readonly<BlogPostContentProps>) => {
  const { blogLayoutId, relatedResources } = post;
  const showRelatedResources = !isWideBlogLayout && relatedResources;
  const showCouponSignupForm =
    post.isCouponUpdate && !post.shouldHideCouponFooter;
  const blogContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const emailSignupElementResult =
      getEmailElementsFromDynamicContent(blogContentRef);

    if (emailSignupElementResult.isError) {
      return;
    }

    addAttributesToDynamicFormContent(emailSignupElementResult.value);

    const { emailSignupButton, emailSignupForm } =
      emailSignupElementResult.value;

    const handleEmailFormSubmit = async (event: SubmitEvent) => {
      event?.preventDefault();

      emailSignupButton.disabled = true;

      const formDataResult = getEmailFormDataFromDynamicBlogContent(event);

      if (formDataResult.isError) {
        return;
      }

      try {
        const request = await fetch("/api/subscribetoallemails", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formDataResult.value,
        });

        const response = await request.json<EmailSignupEndpointResponse>();

        updateDynamicFormContent(emailSignupForm, response.statusCode === 200);
      } catch (exception) {
        logException(exception, "error");
        updateDynamicFormContent(emailSignupForm, false);
      }

      emailSignupButton.disabled = false;
    };

    emailSignupForm.addEventListener("submit", handleEmailFormSubmit);

    return () => {
      if (emailSignupForm) {
        emailSignupForm.removeEventListener("submit", handleEmailFormSubmit);
      }
    };
  }, [post.content]);

  return (
    <>
      <div data-module="blogPostContent">
        <ChildOrNull condition={showTitle}>
          <BlogPostTitleSection post={post} />
        </ChildOrNull>

        <div
          dangerouslySetInnerHTML={{ __html: post.content }}
          ref={blogContentRef}
        />

        <div className="clearfix" />
        {blogLayoutId !== fullWidthLayoutId && (
          <div className="flex flex-col justify-between xs:flex-row xs:items-center">
            <BlogSocialMediaShareIcons
              pinterestShareUrl={post.pinterestShareUrl}
              facebookShareUrl={post.facebookShareUrl}
              twitterShareUrl={post.twitterShareUrl}
            />
          </div>
        )}
        {displayLegalDisclaimer && <ContentLegalDisclaimer />}

        {showCouponSignupForm && (
          <div
            className="rounded-md pb-1"
            style={{ backgroundColor: "#525452" }}
          >
            <h2 className="pb-2 text-center text-white">
              <span>
                <b>Sign up for Emails &amp; Coupons</b>
              </span>
            </h2>
            <p className="text-base mx-12 mb-4 text-center text-white">
              Be the first to know about our coupons, sales, &amp; more!
            </p>
            <BlogCouponCodesSignup containerClasses="w-3/5" />
          </div>
        )}
        {externalLinkModal && <ExternalLinkModal />}
      </div>
      {showRelatedResources && (
        <BlogRelatedResources relatedResources={relatedResources} />
      )}
    </>
  );
};

export default BlogPostContent;
